import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createFormValidator, commonRules } from '@/lib/utils';
import { useSignup } from '@/features/user/hooks/useSignup';
import { useAuth } from '@/hooks/useAuth';
import { FormInput } from '@/features/user/components/signup/FormInput';
import { SubmitButton } from '@/features/user/components/signup/SubmitButton';
import { Toast } from '@/features/user/components/signup/Toast';

interface ConfirmationCodeFormData {
  confirmationCode: string;
}

interface ConfirmationCodeErrors {
  confirmationCode?: string;
}

export const ConfirmationCodeForm: React.FC = () => {
  const navigate = useNavigate();
  const { verifyEmail, pendingSignupData } = useSignup();
  const { clearError } = useAuth();
  const [formData, setFormData] = useState<ConfirmationCodeFormData>({
    confirmationCode: ''
  });
  const [errors, setErrors] = useState<ConfirmationCodeErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);

  // Redirect if no pending signup data (shouldn't be on this page)
  useEffect(() => {
    if (!pendingSignupData) {
      navigate('/signup');
    }
  }, [pendingSignupData, navigate]);

  // Create form validator
  const validator = createFormValidator({
    confirmationCode: commonRules.confirmationCode('Code must be at least 4 characters'),
  });


  const handleInputChange = (field: keyof ConfirmationCodeFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors = validator.validateForm(formData);
    setErrors(newErrors);
    return validator.isFormValid(formData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    clearError();
    
    try {
      const isValid = await verifyEmail(formData.confirmationCode);
      
      if (isValid) {
        // Show success message
        setShowToast(true);
        // Navigation is handled in the hook
      } else {
        // Invalid code
        setErrors(prev => ({ ...prev, confirmationCode: 'Invalid code. Please enter 123456 for development.' }));
      }
      
    } catch (error) {
      console.error('Confirmation error:', error);
      setErrors(prev => ({ ...prev, confirmationCode: 'Invalid code. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Confirmation Code Field */}
        <FormInput
          label="Confirmation Code"
          type="text"
          value={formData.confirmationCode}
          onChange={(value: string) => handleInputChange('confirmationCode', value.replace(/\D/g, '').slice(0, 6))}
          error={errors.confirmationCode}
          placeholder="123456"
          maxLength={6}
          isConfirmationCode
          helpText="Enter the code sent to your email (use 123456 for development)"
        />

        {/* Submit Button */}
        <SubmitButton isSubmitting={isSubmitting} />

        {/* Help Text */}
        <p className="text-xs text-gray-500 text-center mt-6 leading-relaxed">
          Enter the 6-digit code sent to your email address
        </p>
      </form>

      <Toast 
        message="Signed up successfully!" 
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={4000}
      />
    </>
  );
};
